/*----------------------------------
- AGENDA FEEDS DISCLOSURE
----------------------------------*/

/**
 * @docs docs/features/agenda
 * @adr  ADR-0060
 * @rule The eight feed links are plain anchors in the server HTML (inside the closed disclosure),
 *       so crawlers and visitors without JavaScript reach every feed; nothing here needs Radix.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import AgendaBoardPopover from '@uniquedomains/components/agenda/board/AgendaBoardPopover';
import {
    AGENDA_CATEGORIES,
    AGENDA_CATEGORY_LABELS,
    AGENDA_ICS_PATH,
    AGENDA_LAUNCHES_CATEGORY,
    agendaCategoryIcsPath,
    type AgendaCategory,
} from '@uniquedomains/components/contracts/agenda/public';

import { AGENDA_PAGE_CONTENT } from '@/client/catalogs/landing/agenda/pageContent';
import { track } from '@/client/pages/landing/lib/analytics';

import AgendaFeedCopyButton from './AgendaFeedCopyButton';

/*----------------------------------
- CONSTANTS
----------------------------------*/

/**
 * The eight feeds the server answers: everything, TLD launches (the agenda's own category, listed
 * first as in the design), then the six event categories in catalog order.
 */
const AGENDA_ICS_FEEDS: ReadonlyArray<{ category: AgendaCategory | null; href: string; label: string }> = [
    { category: null, href: AGENDA_ICS_PATH, label: AGENDA_PAGE_CONTENT.feeds.allLabel },
    ...[
        ...AGENDA_CATEGORIES.filter((category) => category === AGENDA_LAUNCHES_CATEGORY),
        ...AGENDA_CATEGORIES.filter((category) => category !== AGENDA_LAUNCHES_CATEGORY),
    ].map((category) => ({
        category,
        href: agendaCategoryIcsPath(category),
        label: AGENDA_CATEGORY_LABELS[category],
    })),
];

/*----------------------------------
- COMPONENT
----------------------------------*/

/** The title bar's "Add to calendar": every calendar feed, each with its link to copy. */
export default function AgendaFeedsDisclosure() {
    const content = AGENDA_PAGE_CONTENT.feeds;

    return (
        <AgendaBoardPopover label={content.summary} testId="agenda-ics" id="calendar-feed">
            <p class="m-0 text-[15px] font-bold tracking-[-0.01em] text-[#1b1d1e]">{content.heading}</p>
            <p class="mb-2.5 mt-1.5 text-[12.5px] leading-normal text-[#66696b]">{content.body}</p>
            <ul class="m-0 list-none p-0">
                {AGENDA_ICS_FEEDS.map((feed) => {
                    const feedKey = feed.category ?? 'all';
                    return (
                        <li key={feed.href} class="flex items-center justify-between gap-2.5 border-t border-[rgba(27,29,30,0.08)] py-[7px]">
                            <a
                                href={feed.href}
                                type="text/calendar"
                                class="min-w-0 truncate text-[13.5px] font-semibold text-[#1b1d1e] underline-offset-4 hover:underline"
                                data-testid={`agenda-ics-${feedKey}`}
                                onClick={() => track('agenda_ics_click', { category: feedKey, action: 'open' })}
                            >
                                {feed.label}
                            </a>
                            <AgendaFeedCopyButton href={feed.href} feedKey={feedKey} label={feed.label} />
                        </li>
                    );
                })}
            </ul>
        </AgendaBoardPopover>
    );
}
